import { apiFetch } from '../auth/apiClient'
import type {
  AlertEventsPayload,
  AlertsPayload,
  CreateAlertRequest,
  PriceAlert,
} from '../contracts'

export const ALERTS_UPDATED_EVENT = 'credem:alerts-updated'

function redirectToLogin(): void {
  if (typeof window === 'undefined') return
  const next = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.replace(`/login?next=${next}`)
}

async function handleErrorResponse(response: Response): Promise<never> {
  if (response.status === 401) redirectToLogin()
  let message = `Request failed with status ${response.status}`
  try {
    const payload = (await response.json()) as { error?: string; detail?: string }
    if (payload?.error) message = payload.error
    else if (payload?.detail) message = payload.detail
  } catch {
    // Keep the status-derived fallback.
  }
  throw new Error(message)
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await apiFetch(url, {
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!response.ok) await handleErrorResponse(response)
  return (await response.json()) as T
}

async function sendJson<T>(
  method: 'POST' | 'PATCH',
  url: string,
  body: Record<string, unknown> | null,
  signal?: AbortSignal,
): Promise<T> {
  const response = await apiFetch(url, {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body == null ? undefined : JSON.stringify(body),
    signal,
  })
  if (!response.ok) await handleErrorResponse(response)
  return (await response.json()) as T
}

/**
 * Armed alerts live in the alerts service, so this call can fail independently
 * of {@link fetchAlertEvents}. The route keeps the two boundaries separate on
 * purpose: an alerts-service outage must not blank out fired-event history.
 */
export function fetchAlerts(signal?: AbortSignal): Promise<AlertsPayload> {
  return fetchJson<AlertsPayload>('/api/alerts', signal)
}

/** Fired events, served from our own table — survives an alerts-service outage. */
export function fetchAlertEvents(signal?: AbortSignal): Promise<AlertEventsPayload> {
  return fetchJson<AlertEventsPayload>('/api/alerts/events', signal)
}

export function createAlert(
  payload: CreateAlertRequest,
  signal?: AbortSignal,
): Promise<{ alert: PriceAlert }> {
  return sendJson<{ alert: PriceAlert }>(
    'POST',
    '/api/alerts',
    payload as unknown as Record<string, unknown>,
    signal,
  )
}

export function setAlertStatus(
  alertId: string,
  status: 'active' | 'paused' | 'deleted',
  signal?: AbortSignal,
): Promise<{ alert: PriceAlert }> {
  return sendJson<{ alert: PriceAlert }>(
    'PATCH',
    `/api/alerts/${encodeURIComponent(alertId)}`,
    { status },
    signal,
  )
}

export function markAlertEventsSeen(signal?: AbortSignal): Promise<{ marked: number }> {
  return sendJson<{ marked: number }>('POST', '/api/alerts/events/seen', null, signal)
}
